import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import EndPoints from '../../services/end-points';
import { IWmsDropdownItem } from '../../models/wms/dropdown/response';
import { extractLabelFromName } from '../../utils/helpers/translations';

export interface HierarchicalEntity {
  id: number;
  label: string;
  code?: string;
  hasBins?: boolean;
  hasShelves?: boolean;
  sides?: number;
  sidesLabel?: string;
  side?: number;
  sideLabel?: string;
}

export interface ChainItem {
  label: string;
  value: number;
}

export interface VisibleLevels {
  zone: boolean;
  aisle: boolean;
  storageUnit: boolean;
  shelf: boolean;
  bin: boolean;
}

export interface UseHierarchicalDropdownOptions {
  /** Pre-fetch all dropdowns on mount (default: false) */
  preFetchAll?: boolean;
  /** Enable caching (default: true) */
  enableCache?: boolean;
  /** Debounce delay in ms for filter changes (default: 300) */
  debounceMs?: number;
  /** Enable the storage unit dropdown level (default: true) */
  includeStorageUnitLevel?: boolean;
  /** Filter shelves to only show those with bins (default: true) */
  shelvesWithBinsOnly?: boolean;
}

export interface InitializeForEditParams {
  storeId?: number;
  zoneTypeId?: number;
  zoneId?: number;
  aisleId?: number;
  storageUnitId?: number;
  shelfId?: number;
  sidesFilter?: number;
  sideFilter?: number;
}

export interface UseHierarchicalDropdownReturn {
  // Data - all dropdowns
  stores: HierarchicalEntity[];
  zoneTypes: HierarchicalEntity[];
  zones: HierarchicalEntity[];
  aisles: HierarchicalEntity[];
  storageUnits: HierarchicalEntity[];
  shelves: HierarchicalEntity[];
  bins: HierarchicalEntity[];

  // Loading states
  loadingStores: boolean;
  loadingZoneTypes: boolean;
  loadingZones: boolean;
  loadingAisles: boolean;
  loadingStorageUnits: boolean;
  loadingShelves: boolean;
  loadingBins: boolean;

  // Error states
  errorStores: string | null;
  errorZoneTypes: string | null;
  errorZones: string | null;
  errorAisles: string | null;
  errorStorageUnits: string | null;
  errorShelves: string | null;
  errorBins: string | null;

  // Selected filter values (for filtering dropdowns)
  selectedStoreId: number | undefined;
  selectedZoneTypeId: number | undefined;
  selectedZoneId: number | undefined;
  selectedAisleId: number | undefined;
  selectedStorageUnitId: number | undefined;
  selectedShelfId: number | undefined;
  selectedSidesFilter: number | undefined;
  selectedSideFilter: number | undefined;

  // Setters for filter values
  setSelectedStoreId: (id: number | undefined) => void;
  setSelectedZoneTypeId: (id: number | undefined) => void;
  setSelectedZoneId: (id: number | undefined) => void;
  setSelectedAisleId: (id: number | undefined) => void;
  setSelectedStorageUnitId: (id: number | undefined) => void;
  setSelectedShelfId: (id: number | undefined) => void;
  setSelectedSidesFilter: (id: number | undefined) => void;
  setSelectedSideFilter: (id: number | undefined) => void;

  // Reset helpers
  resetAll: () => void;

  // Filtered dropdown options (memoized based on selected ancestors)
  filteredZones: HierarchicalEntity[];
  filteredAisles: HierarchicalEntity[];
  filteredStorageUnits: HierarchicalEntity[];
  filteredShelves: HierarchicalEntity[];
  filteredBins: HierarchicalEntity[];

  // Refresh functions
  refreshStores: () => Promise<void>;
  refreshZoneTypes: () => Promise<void>;
  refreshZones: () => Promise<void>;
  refreshAisles: () => Promise<void>;
  refreshStorageUnits: () => Promise<void>;
  refreshShelves: () => Promise<void>;
  refreshBins: () => Promise<void>;

  // Progressive visibility
  visibleLevels: VisibleLevels;

  // Selected aisle's layout (derived from selected aisle)
  selectedAisleLayout: number | undefined;

  // Previous chain for edit mode
  previousChain: ChainItem[] | null;
  capturePreviousChain: (chain: ChainItem[]) => void;
  clearPreviousChain: () => void;
  resetFromLevel: (level: 'store' | 'zone' | 'aisle' | 'storageUnit' | 'shelf') => void;

  // Edit mode initialization — sets all selections immediately (no debounce)
  // and loads all needed dropdown options in parallel
  initializeForEdit: (params: InitializeForEditParams) => void;
}

/**
 * Enhanced reusable hook for managing hierarchical dropdown state in WMS
 * 
 * Features:
 * - Pre-fetch all dropdowns on mount
 * - Filter any dropdown by any ancestor (not just immediate parent)
 * - Caching with configurable TTL
 * - Debounced filter changes
 * - Error handling and recovery
 * 
 * Hierarchy: Store → Zone → Aisle → Storage Unit → Shelf → Bin
 * Zone Type is a classification, not part of the hierarchy
 */
export function useHierarchicalDropdown(
  options: UseHierarchicalDropdownOptions = {}
): UseHierarchicalDropdownReturn {
  const {
    preFetchAll = false,
    enableCache = true,
    debounceMs = 300,
    includeStorageUnitLevel = true,
    shelvesWithBinsOnly = true,
  } = options;

  // Data states - all dropdowns
  const [stores, setStores] = useState<HierarchicalEntity[]>([]);
  const [zoneTypes, setZoneTypes] = useState<HierarchicalEntity[]>([]);
  const [zones, setZones] = useState<HierarchicalEntity[]>([]);
  const [aisles, setAisles] = useState<HierarchicalEntity[]>([]);
  const [storageUnits, setStorageUnits] = useState<HierarchicalEntity[]>([]);
  const [shelves, setShelves] = useState<HierarchicalEntity[]>([]);
  const [bins, setBins] = useState<HierarchicalEntity[]>([]);

  // Loading states
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingZoneTypes, setLoadingZoneTypes] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingAisles, setLoadingAisles] = useState(false);
  const [loadingStorageUnits, setLoadingStorageUnits] = useState(false);
  const [loadingShelves, setLoadingShelves] = useState(false);
  const [loadingBins, setLoadingBins] = useState(false);

  // Error states
  const [errorStores, setErrorStores] = useState<string | null>(null);
  const [errorZoneTypes, setErrorZoneTypes] = useState<string | null>(null);
  const [errorZones, setErrorZones] = useState<string | null>(null);
  const [errorAisles, setErrorAisles] = useState<string | null>(null);
  const [errorStorageUnits, setErrorStorageUnits] = useState<string | null>(null);
  const [errorShelves, setErrorShelves] = useState<string | null>(null);
  const [errorBins, setErrorBins] = useState<string | null>(null);

  // Selected filter values
  const [selectedStoreId, setSelectedStoreIdState] = useState<number | undefined>();
  const [selectedZoneTypeId, setSelectedZoneTypeIdState] = useState<number | undefined>();
  const [selectedZoneId, setSelectedZoneIdState] = useState<number | undefined>();
  const [selectedAisleId, setSelectedAisleIdState] = useState<number | undefined>();
  const [selectedStorageUnitId, setSelectedStorageUnitIdState] = useState<number | undefined>();
  const [selectedShelfId, setSelectedShelfIdState] = useState<number | undefined>();
  const [selectedSidesFilter, setSelectedSidesFilterState] = useState<number | undefined>();
  const [selectedSideFilter, setSelectedSideFilterState] = useState<number | undefined>();

  // Progressive visibility state
  const [visibleLevels, setVisibleLevels] = useState<VisibleLevels>({
    zone: false,
    aisle: false,
    storageUnit: false,
    shelf: false,
    bin: false,
  });

  // Previous chain for edit mode
  const [previousChain, setPreviousChain] = useState<ChainItem[] | null>(null);

  // Cache storage
  const cacheRef = useRef<Record<string, { data: HierarchicalEntity[]; timestamp: number }>>({});
  
  // Abort controllers for canceling requests
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  
  // Debounce timers
  const debounceTimersRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Guard: when initializeForEdit runs, skip the next reactive effect cycle
  const skipReactiveLoadRef = useRef(false);

  // Cache helpers
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const getCached = useCallback((key: string): HierarchicalEntity[] | undefined => {
    if (!enableCache) return undefined;
    const cached = cacheRef.current[key];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    if (cached) {
      delete cacheRef.current[key];
    }
    return undefined;
  }, [enableCache]);

  const setCached = useCallback((key: string, data: HierarchicalEntity[]) => {
    if (enableCache) {
      cacheRef.current[key] = { data, timestamp: Date.now() };
    }
  }, [enableCache]);

  // Cancel request helper
  const cancelRequest = useCallback((key: string) => {
    if (abortControllersRef.current[key]) {
      abortControllersRef.current[key].abort();
      delete abortControllersRef.current[key];
    }
  }, []);

  // Clear debounce timer helper
  const clearDebounceTimer = useCallback((key: string) => {
    if (debounceTimersRef.current[key]) {
      clearTimeout(debounceTimersRef.current[key]);
      delete debounceTimersRef.current[key];
    }
  }, []);

  // Load stores
  const loadStores = useCallback(async (force = false) => {
    const key = 'stores';
    const cached = getCached(key);
    if (cached && !force) {
      setStores(cached);
      return;
    }

    cancelRequest(key);
    const abortController = new AbortController();
    abortControllersRef.current[key] = abortController;

    setLoadingStores(true);
    setErrorStores(null);

    try {
      const { data } = await EndPoints.wmsShared.getDropdownStores({ signal: abortController.signal });
      const mapped = data.data.map((s: any) => ({ id: s.id, label: s.name }));
      setStores(mapped);
      setCached(key, mapped);
    } catch (error: any) {
      if (!axios.isCancel(error) && error.name !== 'AbortError') {
        console.error('Failed to load stores:', error);
        setErrorStores(error.message || 'Failed to load stores');
        setStores([]);
      }
    } finally {
      setLoadingStores(false);
      delete abortControllersRef.current[key];
    }
  }, [getCached, setCached, cancelRequest]);

  // Load zone types
  const loadZoneTypes = useCallback(async (force = false) => {
    const key = 'zoneTypes';
    const cached = getCached(key);
    if (cached && !force) {
      setZoneTypes(cached);
      return;
    }

    cancelRequest(key);
    const abortController = new AbortController();
    abortControllersRef.current[key] = abortController;

    setLoadingZoneTypes(true);
    setErrorZoneTypes(null);

    try {
      const { data } = await EndPoints.wmsShared.getDropdownZoneTypes({ signal: abortController.signal });
      const mapped = data.data.map((item: any) => ({
        id: item.id,
        label: typeof item.name === 'object' ? item.name.en || item.name.ar : item.name,
      }));
      setZoneTypes(mapped);
      setCached(key, mapped);
    } catch (error: any) {
      if (!axios.isCancel(error) && error.name !== 'AbortError') {
        console.error('Failed to load zone types:', error);
        setErrorZoneTypes(error.message || 'Failed to load zone types');
        setZoneTypes([]);
      }
    } finally {
      setLoadingZoneTypes(false);
      delete abortControllersRef.current[key];
    }
  }, [getCached, setCached, cancelRequest]);

  // Load zones with ancestor filters
  const loadZones = useCallback(async (force = false) => {
    const filterKey = `zones:${selectedStoreId || ''}:${selectedZoneTypeId || ''}`;
    const cached = getCached(filterKey);
    if (cached && !force) {
      setZones(cached);
      return;
    }

    cancelRequest(filterKey);
    const abortController = new AbortController();
    abortControllersRef.current[filterKey] = abortController;

    setLoadingZones(true);
    setErrorZones(null);

    try {
      const { data } = await EndPoints.wmsShared.getDropdownZones(
        {
          storeId: selectedStoreId,
          zoneTypeId: selectedZoneTypeId,
        },
        { signal: abortController.signal }
      );
      const mapped = data.data.map((z: any) => ({
        id: z.id,
        label: extractLabelFromName(z.name, `Zone ${z.id}`),
      }));
      setZones(mapped);
      setCached(filterKey, mapped);
    } catch (error: any) {
      if (!axios.isCancel(error) && error.name !== 'AbortError') {
        console.error('Failed to load zones:', error);
        setErrorZones(error.message || 'Failed to load zones');
        setZones([]);
      }
    } finally {
      setLoadingZones(false);
      delete abortControllersRef.current[filterKey];
    }
  }, [selectedStoreId, selectedZoneTypeId, getCached, setCached, cancelRequest]);

  // Load aisles with ancestor filters
  const loadAisles = useCallback(async (force = false) => {
    const filterKey = `aisles:${selectedStoreId || ''}:${selectedZoneId || ''}:${selectedSidesFilter || ''}`;
    const cached = getCached(filterKey);
    if (cached && !force) {
      setAisles(cached);
      return;
    }

    cancelRequest(filterKey);
    const abortController = new AbortController();
    abortControllersRef.current[filterKey] = abortController;

    setLoadingAisles(true);
    setErrorAisles(null);

    try {
      const { data } = await EndPoints.wmsShared.getDropdownAisles(
        {
          storeId: selectedStoreId,
          zoneId: selectedZoneId,
          sides: selectedSidesFilter,
        },
        { signal: abortController.signal }
      );
      const mapped = data.data.map((a: any) => ({
        id: a.id,
        label: extractLabelFromName(a.name, `Aisle ${a.id}`),
        sides: a.sides,
      }));
      setAisles(mapped);
      setCached(filterKey, mapped);
    } catch (error: any) {
      if (!axios.isCancel(error) && error.name !== 'AbortError') {
        console.error('Failed to load aisles:', error);
        setErrorAisles(error.message || 'Failed to load aisles');
        setAisles([]);
      }
    } finally {
      setLoadingAisles(false);
      delete abortControllersRef.current[filterKey];
    }
  }, [selectedStoreId, selectedZoneId, selectedSidesFilter, getCached, setCached, cancelRequest]);

  // Load storage units with ancestor filters
  const loadStorageUnits = useCallback(async (force = false) => {
    const filterKey = `storageUnits:${selectedStoreId || ''}:${selectedZoneId || ''}:${selectedAisleId || ''}:${selectedSideFilter || ''}`;
    const cached = getCached(filterKey);
    if (cached && !force) {
      setStorageUnits(cached);
      return;
    }

    cancelRequest(filterKey);
    const abortController = new AbortController();
    abortControllersRef.current[filterKey] = abortController;

    setLoadingStorageUnits(true);
    setErrorStorageUnits(null);

    try {
      const { data } = await EndPoints.wmsShared.getDropdownStorageUnits(
        {
          storeId: selectedStoreId,
          zoneId: selectedZoneId,
          aisleId: selectedAisleId,
          side: selectedSideFilter,
        },
        { signal: abortController.signal }
      );
      const mapped = data.data.map((su: any) => ({
        id: su.id,
        label: extractLabelFromName(su.name, `Storage Unit ${su.id}`),
        side: su.side,
      }));
      setStorageUnits(mapped);
      setCached(filterKey, mapped);
    } catch (error: any) {
      if (!axios.isCancel(error) && error.name !== 'AbortError') {
        console.error('Failed to load storage units:', error);
        setErrorStorageUnits(error.message || 'Failed to load storage units');
        setStorageUnits([]);
      }
    } finally {
      setLoadingStorageUnits(false);
      delete abortControllersRef.current[filterKey];
    }
  }, [selectedStoreId, selectedZoneId, selectedAisleId, selectedSideFilter, getCached, setCached, cancelRequest]);

  // Load shelves with ancestor filters
  const loadShelves = useCallback(async (force = false) => {
    const filterKey = `shelves:${selectedStoreId || ''}:${selectedZoneId || ''}:${selectedAisleId || ''}:${selectedStorageUnitId || ''}`;
    const cached = getCached(filterKey);
    if (cached && !force) {
      setShelves(cached);
      return;
    }

    cancelRequest(filterKey);
    const abortController = new AbortController();
    abortControllersRef.current[filterKey] = abortController;

    setLoadingShelves(true);
    setErrorShelves(null);

    try {
      const { data } = await EndPoints.wmsShared.getDropdownShelves(
        {
          storeId: selectedStoreId,
          zoneId: selectedZoneId,
          aisleId: selectedAisleId,
          storageUnitId: selectedStorageUnitId,
          hasBins: shelvesWithBinsOnly ? true : undefined,
        },
        { signal: abortController.signal }
      );
      const mapped = data.data.map((s: any) => ({
        id: s.id,
        label: extractLabelFromName(s.name, `Shelf ${s.id}`),
      }));
      setShelves(mapped);
      setCached(filterKey, mapped);
    } catch (error: any) {
      if (!axios.isCancel(error) && error.name !== 'AbortError') {
        console.error('Failed to load shelves:', error);
        setErrorShelves(error.message || 'Failed to load shelves');
        setShelves([]);
      }
    } finally {
      setLoadingShelves(false);
      delete abortControllersRef.current[filterKey];
    }
  }, [selectedStoreId, selectedZoneId, selectedAisleId, selectedStorageUnitId, shelvesWithBinsOnly, getCached, setCached, cancelRequest]);

  // Load bins with ancestor filters
  const loadBins = useCallback(async (force = false) => {
    const filterKey = `bins:${selectedStoreId || ''}:${selectedZoneId || ''}:${selectedAisleId || ''}:${selectedStorageUnitId || ''}:${selectedShelfId || ''}`;
    const cached = getCached(filterKey);
    if (cached && !force) {
      setBins(cached);
      return;
    }

    cancelRequest(filterKey);
    const abortController = new AbortController();
    abortControllersRef.current[filterKey] = abortController;

    setLoadingBins(true);
    setErrorBins(null);

    try {
      const { data } = await EndPoints.wmsShared.getDropdownBins(
        {
          storeId: selectedStoreId,
          zoneId: selectedZoneId,
          aisleId: selectedAisleId,
          storageUnitId: selectedStorageUnitId,
          shelfId: selectedShelfId,
        },
        { signal: abortController.signal }
      );
      const mapped = data.data.map((b: any) => ({
        id: b.id,
        label: extractLabelFromName(b.name, `Bin ${b.id}`),
      }));
      setBins(mapped);
      setCached(filterKey, mapped);
    } catch (error: any) {
      if (!axios.isCancel(error) && error.name !== 'AbortError') {
        console.error('Failed to load bins:', error);
        setErrorBins(error.message || 'Failed to load bins');
        setBins([]);
      }
    } finally {
      setLoadingBins(false);
      delete abortControllersRef.current[filterKey];
    }
  }, [selectedStoreId, selectedZoneId, selectedAisleId, selectedStorageUnitId, selectedShelfId, getCached, setCached, cancelRequest]);

  // Initial load
  useEffect(() => {
    loadStores();
    loadZoneTypes();
    if (preFetchAll) {
      loadZones();
      loadAisles();
      if (includeStorageUnitLevel) {
        loadStorageUnits();
        loadShelves();
        loadBins();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when filters change — load child data when parent is selected
  // Skip when initializeForEdit already handled loading
  useEffect(() => {
    if (skipReactiveLoadRef.current) return;
    if (preFetchAll || selectedStoreId || selectedZoneTypeId) {
      loadZones();
    } else {
      setZones([]);
    }
  }, [selectedStoreId, selectedZoneTypeId, preFetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (skipReactiveLoadRef.current) return;
    if (preFetchAll || selectedZoneId) {
      setAisles([]);
      loadAisles();
    } else {
      setAisles([]);
    }
  }, [selectedStoreId, selectedZoneId, selectedSidesFilter, preFetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (skipReactiveLoadRef.current) return;
    if (!includeStorageUnitLevel) {
      setStorageUnits([]);
      return;
    }
    if (preFetchAll || selectedAisleId) {
      setStorageUnits([]);
      loadStorageUnits();
    } else {
      setStorageUnits([]);
    }
  }, [selectedStoreId, selectedZoneId, selectedAisleId, selectedSideFilter, preFetchAll, includeStorageUnitLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (skipReactiveLoadRef.current) return;
    if (preFetchAll || selectedStorageUnitId) {
      setShelves([]);
      loadShelves();
    } else {
      setShelves([]);
    }
  }, [selectedStoreId, selectedZoneId, selectedAisleId, selectedStorageUnitId, preFetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (skipReactiveLoadRef.current) return;
    if (preFetchAll || selectedShelfId) {
      setBins([]);
      loadBins();
    } else {
      setBins([]);
    }
  }, [selectedStoreId, selectedZoneId, selectedAisleId, selectedStorageUnitId, selectedShelfId, preFetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Setters with debounce
  const setSelectedStoreId = useCallback((id: number | undefined) => {
    clearDebounceTimer('storeId');
    debounceTimersRef.current['storeId'] = setTimeout(() => {
      setSelectedStoreIdState(id);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  const setSelectedZoneTypeId = useCallback((id: number | undefined) => {
    clearDebounceTimer('zoneTypeId');
    debounceTimersRef.current['zoneTypeId'] = setTimeout(() => {
      setSelectedZoneTypeIdState(id);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  const setSelectedZoneId = useCallback((id: number | undefined) => {
    clearDebounceTimer('zoneId');
    debounceTimersRef.current['zoneId'] = setTimeout(() => {
      setSelectedZoneIdState(id);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  const setSelectedAisleId = useCallback((id: number | undefined) => {
    clearDebounceTimer('aisleId');
    debounceTimersRef.current['aisleId'] = setTimeout(() => {
      setSelectedAisleIdState(id);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  const setSelectedStorageUnitId = useCallback((id: number | undefined) => {
    clearDebounceTimer('storageUnitId');
    debounceTimersRef.current['storageUnitId'] = setTimeout(() => {
      setSelectedStorageUnitIdState(id);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  const setSelectedShelfId = useCallback((id: number | undefined) => {
    clearDebounceTimer('shelfId');
    debounceTimersRef.current['shelfId'] = setTimeout(() => {
      setSelectedShelfIdState(id);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  const setSelectedSidesFilter = useCallback((val: number | undefined) => {
    clearDebounceTimer('sidesFilter');
    debounceTimersRef.current['sidesFilter'] = setTimeout(() => {
      setSelectedSidesFilterState(val);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  const setSelectedSideFilter = useCallback((val: number | undefined) => {
    clearDebounceTimer('sideFilter');
    debounceTimersRef.current['sideFilter'] = setTimeout(() => {
      setSelectedSideFilterState(val);
    }, debounceMs);
  }, [debounceMs, clearDebounceTimer]);

  // Reset helper
  const resetAll = useCallback(() => {
    setSelectedStoreIdState(undefined);
    setSelectedZoneTypeIdState(undefined);
    setSelectedZoneIdState(undefined);
    setSelectedAisleIdState(undefined);
    setSelectedStorageUnitIdState(undefined);
    setSelectedShelfIdState(undefined);
    setSelectedSidesFilterState(undefined);
    setSelectedSideFilterState(undefined);
  }, []);

  // Memoized filtered dropdowns (for non-preFetchAll mode)
  const filteredZones = useMemo(() => {
    if (preFetchAll) return zones;
    return zones.filter(z => {
      if (selectedStoreId && z.id !== selectedStoreId) return true; // zones don't have storeId in response
      return true;
    });
  }, [zones, preFetchAll, selectedStoreId]);

  const filteredAisles = useMemo(() => aisles, [aisles]);
  const filteredStorageUnits = useMemo(() => storageUnits, [storageUnits]);
  const filteredShelves = useMemo(() => shelves, [shelves]);
  const filteredBins = useMemo(() => bins, [bins]);

  // Refresh functions
  const refreshStores = useCallback(() => loadStores(true), [loadStores]);
  const refreshZoneTypes = useCallback(() => loadZoneTypes(true), [loadZoneTypes]);
  const refreshZones = useCallback(() => loadZones(true), [loadZones]);
  const refreshAisles = useCallback(() => loadAisles(true), [loadAisles]);
  const refreshStorageUnits = useCallback(() => loadStorageUnits(true), [loadStorageUnits]);
  const refreshShelves = useCallback(() => loadShelves(true), [loadShelves]);
  const refreshBins = useCallback(() => loadBins(true), [loadBins]);

  // Progressive visibility: Update visible levels based on selections
  useEffect(() => {
    setVisibleLevels({
      zone: !!selectedStoreId,
      aisle: !!selectedZoneId,
      storageUnit: includeStorageUnitLevel && !!selectedAisleId,
      shelf: includeStorageUnitLevel && !!selectedStorageUnitId,
      bin: !!selectedShelfId,
    });
  }, [selectedStoreId, selectedZoneId, selectedAisleId, selectedStorageUnitId, selectedShelfId, includeStorageUnitLevel]);

  // Capture previous chain for edit mode
  const capturePreviousChain = useCallback((chain: ChainItem[]) => {
    setPreviousChain(chain);
  }, []);

  // Clear previous chain
  const clearPreviousChain = useCallback(() => {
    setPreviousChain(null);
  }, []);

  // Reset from a specific level and hide children
  const resetFromLevel = useCallback((level: 'store' | 'zone' | 'aisle' | 'storageUnit' | 'shelf') => {
    switch (level) {
      case 'store':
        setSelectedStoreIdState(undefined);
        setSelectedZoneTypeIdState(undefined);
        setSelectedZoneIdState(undefined);
        setSelectedAisleIdState(undefined);
        setSelectedStorageUnitIdState(undefined);
        setSelectedShelfIdState(undefined);
        break;
      case 'zone':
        setSelectedZoneIdState(undefined);
        setSelectedAisleIdState(undefined);
        setSelectedStorageUnitIdState(undefined);
        setSelectedShelfIdState(undefined);
        break;
      case 'aisle':
        setSelectedAisleIdState(undefined);
        setSelectedStorageUnitIdState(undefined);
        setSelectedShelfIdState(undefined);
        break;
      case 'storageUnit':
        setSelectedStorageUnitIdState(undefined);
        setSelectedShelfIdState(undefined);
        break;
      case 'shelf':
        setSelectedShelfIdState(undefined);
        break;
    }
  }, []);

  // Derived: selected aisle's layout property
  const selectedAisleLayout = useMemo(() => {
    if (!selectedAisleId) return undefined;
    const aisle = aisles.find(a => a.id === selectedAisleId);
    return aisle?.sides; // The API property is still 'sides'
  }, [selectedAisleId, aisles]);

  // Edit-mode initialization: sets all selections immediately (no debounce)
  // and loads all needed dropdown options in parallel using explicit filter params.
  const initializeForEdit = useCallback((params: InitializeForEditParams) => {
    const {
      storeId,
      zoneTypeId,
      zoneId,
      aisleId,
      storageUnitId,
      shelfId,
      sidesFilter,
      sideFilter,
    } = params;

    // Prevent reactive useEffects from wiping data we're about to load
    skipReactiveLoadRef.current = true;

    // 1. Set all selections immediately (bypass debounce)
    setSelectedStoreIdState(storeId);
    setSelectedZoneTypeIdState(zoneTypeId);
    setSelectedZoneIdState(zoneId);
    setSelectedAisleIdState(aisleId);
    setSelectedStorageUnitIdState(storageUnitId);
    setSelectedShelfIdState(shelfId);
    setSelectedSidesFilterState(sidesFilter);
    setSelectedSideFilterState(sideFilter);

    // 2. Load all needed dropdown options in parallel using explicit params.
    //    The regular loadX() functions use closures over selectedXId state which
    //    hasn't been committed yet, so we call the API directly here.
    const loadPromises: Promise<void>[] = [];

    if (storeId || zoneTypeId) {
      loadPromises.push(
        (async () => {
          setLoadingZones(true);
          try {
            const { data } = await EndPoints.wmsShared.getDropdownZones({ storeId, zoneTypeId });
            const mapped = data.data.map((z: any) => ({
              id: z.id,
              label: extractLabelFromName(z.name, `Zone ${z.id}`),
            }));
            setZones(mapped);
            const cacheKey = `zones:${storeId || ''}:${zoneTypeId || ''}`;
            setCached(cacheKey, mapped);
          } catch (e: any) {
            if (!axios.isCancel(e)) setZones([]);
          } finally {
            setLoadingZones(false);
          }
        })()
      );
    }

    if (zoneId) {
      loadPromises.push(
        (async () => {
          setLoadingAisles(true);
          try {
            const { data } = await EndPoints.wmsShared.getDropdownAisles({
              storeId,
              zoneId,
              sides: sidesFilter,
            });
            const mapped = data.data.map((a: any) => ({
              id: a.id,
              label: extractLabelFromName(a.name, `Aisle ${a.id}`),
              sides: a.sides,
            }));
            setAisles(mapped);
            const cacheKey = `aisles:${storeId || ''}:${zoneId || ''}:${sidesFilter || ''}`;
            setCached(cacheKey, mapped);
          } catch (e: any) {
            if (!axios.isCancel(e)) setAisles([]);
          } finally {
            setLoadingAisles(false);
          }
        })()
      );
    }

    if (aisleId) {
      loadPromises.push(
        (async () => {
          setLoadingStorageUnits(true);
          try {
            const { data } = await EndPoints.wmsShared.getDropdownStorageUnits({
              storeId,
              zoneId,
              aisleId,
              side: sideFilter,
            });
            const mapped = data.data.map((su: any) => ({
              id: su.id,
              label: extractLabelFromName(su.name, `Storage Unit ${su.id}`),
              side: su.side,
            }));
            setStorageUnits(mapped);
            const cacheKey = `storageUnits:${storeId || ''}:${zoneId || ''}:${aisleId || ''}:${sideFilter || ''}`;
            setCached(cacheKey, mapped);
          } catch (e: any) {
            if (!axios.isCancel(e)) setStorageUnits([]);
          } finally {
            setLoadingStorageUnits(false);
          }
        })()
      );
    }

    if (storageUnitId) {
      loadPromises.push(
        (async () => {
          setLoadingShelves(true);
          try {
            const { data } = await EndPoints.wmsShared.getDropdownShelves({
              storeId, zoneId, aisleId, storageUnitId,
              hasBins: shelvesWithBinsOnly ? true : undefined,
            });
            const mapped = data.data.map((s: any) => ({
              id: s.id,
              label: extractLabelFromName(s.name, `Shelf ${s.id}`),
            }));
            setShelves(mapped);
            const cacheKey = `shelves:${storeId || ''}:${zoneId || ''}:${aisleId || ''}:${storageUnitId || ''}`;
            setCached(cacheKey, mapped);
          } catch (e: any) {
            if (!axios.isCancel(e)) setShelves([]);
          } finally {
            setLoadingShelves(false);
          }
        })()
      );
    }

    if (shelfId) {
      loadPromises.push(
        (async () => {
          setLoadingBins(true);
          try {
            const { data } = await EndPoints.wmsShared.getDropdownBins({
              storeId, zoneId, aisleId, storageUnitId, shelfId,
            });
            const mapped = data.data.map((b: any) => ({
              id: b.id,
              label: extractLabelFromName(b.name, `Bin ${b.id}`),
            }));
            setBins(mapped);
            const cacheKey = `bins:${storeId || ''}:${zoneId || ''}:${aisleId || ''}:${storageUnitId || ''}:${shelfId || ''}`;
            setCached(cacheKey, mapped);
          } catch (e: any) {
            if (!axios.isCancel(e)) setBins([]);
          } finally {
            setLoadingBins(false);
          }
        })()
      );
    }

    // Clear the skip flag after React commits state and fires reactive effects.
    // requestAnimationFrame runs after the browser paint, which is after React's
    // useEffect cycle, ensuring all reactive effects have been skipped.
    requestAnimationFrame(() => {
      skipReactiveLoadRef.current = false;
    });
  }, [setCached, shelvesWithBinsOnly]);

  return {
    // Data
    stores,
    zoneTypes,
    zones,
    aisles,
    storageUnits,
    shelves,
    bins,
    
    // Loading states
    loadingStores,
    loadingZoneTypes,
    loadingZones,
    loadingAisles,
    loadingStorageUnits,
    loadingShelves,
    loadingBins,
    
    // Error states
    errorStores,
    errorZoneTypes,
    errorZones,
    errorAisles,
    errorStorageUnits,
    errorShelves,
    errorBins,
    
    // Selected filter values
    selectedStoreId,
    selectedZoneTypeId,
    selectedZoneId,
    selectedAisleId,
    selectedStorageUnitId,
    selectedShelfId,
    selectedSidesFilter,
    selectedSideFilter,
    
    // Setters
    setSelectedStoreId,
    setSelectedZoneTypeId,
    setSelectedZoneId,
    setSelectedAisleId,
    setSelectedStorageUnitId,
    setSelectedShelfId,
    setSelectedSidesFilter,
    setSelectedSideFilter,
    
    // Reset
    resetAll,
    
    // Filtered dropdowns
    filteredZones,
    filteredAisles,
    filteredStorageUnits,
    filteredShelves,
    filteredBins,
    
    // Refresh functions
    refreshStores,
    refreshZoneTypes,
    refreshZones,
    refreshAisles,
    refreshStorageUnits,
    refreshShelves,
    refreshBins,

    // Progressive visibility
    visibleLevels,

    // Selected aisle's layout
    selectedAisleLayout,

    // Previous chain
    previousChain,
    capturePreviousChain,
    clearPreviousChain,
    resetFromLevel,

    // Edit mode initialization
    initializeForEdit,
  };
}

export default useHierarchicalDropdown;
